import React from "react";
import PropTypes from "prop-types";
import {
  DEFAULT_WIDGET_CONFIG,
  UI_CONSTANTS,
  MODEL_FAMILIES,
  CREDIT_EXHAUSTION_OPTIONS,
} from "../../utils/constants";

const AIBehaviorStep = ({
  formData,
  errors,
  onInputChange,
  models,
  creditPrices,
  creditPricesAvailable,
  credits,
  creditsLoading,
  creditsError,
}) => {
  // Build model option label with credit cost if available
  const getModelLabel = (model) => {
    if (!creditPrices?.[model.id]) return model.name;
    return `${model.name} (${creditPrices[model.id]} credit${creditPrices[model.id] === 1 ? "" : "s"}/message)`;
  };

  const handleExhaustionChange = (family, value) => {
    onInputChange("credit_exhaustion_behavior", {
      ...formData.credit_exhaustion_behavior,
      [family]: value,
    });
  };

  return (
    <div className="widget-form-section">
      <div className="widget-form-field">
        <label htmlFor="system_prompt">
          System Prompt <span className="required">*</span>
        </label>
        <textarea
          id="system_prompt"
          value={formData.system_prompt}
          onChange={(e) => onInputChange("system_prompt", e.target.value)}
          placeholder="You are a helpful AI assistant..."
          rows={5}
          className={errors.system_prompt ? "error" : ""}
        />
        {errors.system_prompt && (
          <span className="widget-form-error">{errors.system_prompt}</span>
        )}
        <small>
          Instructions that define the AI's behavior and personality
        </small>
      </div>

      <div className="widget-form-field">
        <label htmlFor="model">Claude Model</label>
        <select
          id="model"
          value={formData.model}
          onChange={(e) => onInputChange("model", e.target.value)}
        >
          {!formData.model && (
            <option value="" disabled>
              Select a model...
            </option>
          )}
          {models.map((m) => (
            <option key={m.id} value={m.id}>
              {getModelLabel(m)}
            </option>
          ))}
        </select>
        <small>Which Claude model to use for responses</small>
      </div>

      {/* Warning: credit prices unavailable */}
      {creditPricesAvailable === false && (
        <div className="widget-form-field">
          <small className="credit-prices-warning">
            Credit pricing could not be loaded. Model costs may not be shown.
          </small>
        </div>
      )}

      {/* Credit balances summary */}
      {creditsLoading && (
        <div className="widget-form-field">
          <label>Your Credit Balances</label>
          <div className="credit-balances-summary">
            <span className="credit-balance-loading">Loading balances...</span>
          </div>
        </div>
      )}
      {creditsError && !credits && (
        <div className="widget-form-field">
          <label>Your Credit Balances</label>
          <div className="credit-balances-summary credit-balances-error">
            <span>{creditsError}</span>
          </div>
        </div>
      )}
      {!creditsLoading && credits && (
        <div className="widget-form-field">
          <label>Your Credit Balances</label>
          <div className="credit-balances-summary">
            {MODEL_FAMILIES.map((family) => {
              const balance = credits[family.key] ?? 0;
              const cost = creditPrices?.[family.key];
              const messagesRemaining = cost
                ? Math.floor(balance / cost)
                : null;
              return (
                <div key={family.key} className="credit-balance-item">
                  <span
                    className="credit-balance-dot"
                    style={{ backgroundColor: family.color }}
                  />
                  <span className="credit-balance-label">{family.label}</span>
                  <span className="credit-balance-value">
                    {balance} credit{balance === 1 ? "" : "s"}
                    {messagesRemaining !== null && (
                      <span className="credit-balance-messages">
                        {" "}
                        ({messagesRemaining} message
                        {messagesRemaining === 1 ? "" : "s"})
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          <small>Credits are deducted per message. Cost varies by model.</small>
        </div>
      )}

      <div className="widget-form-field">
        <label htmlFor="temperature">Temperature: {formData.temperature}</label>
        <input
          id="temperature"
          type="range"
          min="0"
          max="1"
          step={UI_CONSTANTS.TEMPERATURE_STEP}
          value={formData.temperature}
          onChange={(e) =>
            onInputChange("temperature", Number.parseFloat(e.target.value))
          }
          className={errors.temperature ? "error" : ""}
        />
        {errors.temperature && (
          <span className="widget-form-error">{errors.temperature}</span>
        )}
        <small>Higher = more creative, Lower = more focused (0-1)</small>
      </div>

      <div className="widget-form-field">
        <label htmlFor="max_tokens">Max Tokens</label>
        <input
          id="max_tokens"
          type="number"
          value={formData.max_tokens}
          onChange={(e) => {
            const val = Number.parseInt(e.target.value, 10);
            onInputChange(
              "max_tokens",
              Number.isNaN(val)
                ? DEFAULT_WIDGET_CONFIG.MAX_TOKENS_DEFAULT
                : val,
            );
          }}
          min={DEFAULT_WIDGET_CONFIG.MAX_TOKENS_MIN}
          max={DEFAULT_WIDGET_CONFIG.MAX_TOKENS_MAX}
          className={errors.max_tokens ? "error" : ""}
        />
        {errors.max_tokens && (
          <span className="widget-form-error">{errors.max_tokens}</span>
        )}
        <small>
          Maximum length of AI responses ({DEFAULT_WIDGET_CONFIG.MAX_TOKENS_MIN}
          -{DEFAULT_WIDGET_CONFIG.MAX_TOKENS_MAX})
        </small>
      </div>

      {/* Credit Exhaustion Behavior */}
      {formData.credit_exhaustion_behavior && (
        <>
          <h3 className="widget-form-section-title">When Credits Run Out</h3>
          <small className="credit-exhaustion-description">
            Choose what happens for each model when its credits are exhausted.
            "Auto-downgrade" tries the next cheaper model (Opus → Sonnet → Haiku
            → Block).
          </small>
          {/* Audit 3.4: reverse order is intentional — shows Opus first to match
              the downgrade chain (Opus → Sonnet → Haiku → Block). Not a bug. */}
          {MODEL_FAMILIES.slice()
            .reverse()
            .map((family) => (
              <div key={family.key} className="widget-form-field">
                <label htmlFor={`exhaustion-${family.key}`}>
                  <span
                    className="credit-balance-dot"
                    style={{ backgroundColor: family.color, marginRight: 6 }}
                  />
                  {family.label}
                </label>
                <select
                  id={`exhaustion-${family.key}`}
                  value={
                    formData.credit_exhaustion_behavior[family.key] || "block"
                  }
                  onChange={(e) =>
                    handleExhaustionChange(family.key, e.target.value)
                  }
                >
                  {CREDIT_EXHAUSTION_OPTIONS.filter((opt) => {
                    // "downgrade" not available for haiku (cheapest model — nowhere to downgrade)
                    return !(
                      family.key === "haiku" && opt.value === "downgrade"
                    );
                  }).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
        </>
      )}
    </div>
  );
};

AIBehaviorStep.propTypes = {
  formData: PropTypes.shape({
    system_prompt: PropTypes.string,
    model: PropTypes.string,
    temperature: PropTypes.number,
    max_tokens: PropTypes.number,
    credit_exhaustion_behavior: PropTypes.object,
  }).isRequired,
  errors: PropTypes.object.isRequired,
  onInputChange: PropTypes.func.isRequired,
  models: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  creditPrices: PropTypes.object,
  creditPricesAvailable: PropTypes.bool,
  credits: PropTypes.object,
  creditsLoading: PropTypes.bool,
  creditsError: PropTypes.string,
};

export default AIBehaviorStep;
